import { useFpCampaignCartSelector } from '@cart-services/hooks/useFpCampaignCartStore'
import { useFpCampaignInvoiceService } from '@cart-services/hooks/useFpCampaignInvoiceService'
import { useFpCampaignProductsService } from '@cart-services/hooks/useFpCampaignProductsService'
import { type CountryCode } from '@constants'
import { useEffectOnce } from '@dni-react-hooks/use-effect-once'
import { ReceiptType } from '@hooks-api/types/dataModel/eCommercePayments'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import { CarrierInput } from '@wp-checkout/components/InvoiceTypes/PhoneCarrierField/CarrierInput'
import { type ChangeEvent, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { type z } from 'zod'
import { useCampaign } from '../hooks/use-campaign'
import { useBrand } from '../hooks/useBrand'
import { useCampaignCarrierFieldValidation } from '../pages/campaign/checkout/use-campaign-carrier-field-validation'
import { PaymentContext } from '../pages/campaign/client-payments'
import style from '../style/components/online-invoices.module.scss'

interface Props {
  isInvoiceEnable: boolean
  pickedPayment: string
  campaignId: number
  invoiceError: z.ZodError | null
  countryCode: CountryCode
}

type Invoices = Record<string, string>

export const OnlineInvoices = (props: Props) => {
  const { isInvoiceEnable, pickedPayment, campaignId, invoiceError, countryCode } = props
  const { setIsCheckoutDisabledConditions } = useContext(PaymentContext)
  const { t } = useTranslation()
  const invoiceInfo = useFpCampaignCartSelector(campaignId, state => state.invoice)
  const { setReceiptType, setPhoneCarrierNumber, setBusinessUbnTitle, setBusinessUbn, setRefundStatement, setCarrierValidationStatus } = useFpCampaignInvoiceService(campaignId)

  const { brandMap } = useBrand()
  const { campaignCodeFromUrl } = useCampaign()
  const { projectCode } = useFpCampaignProductsService(campaignCodeFromUrl)
  const company = brandMap?.[projectCode || '']?.company_name

  const carrierStatus = useCampaignCarrierFieldValidation(countryCode, invoiceInfo.phoneCarrierNumber)

  useEffect(function syncCarrierStatusToStore() {
    setCarrierValidationStatus(carrierStatus)
  }, [carrierStatus, setCarrierValidationStatus])

  useEffect(() => {
    if (
      pickedPayment?.includes('offline')
      || invoiceInfo.receiptType === 'non_business_einvoice'
      || (invoiceInfo.receiptType === 'phone_carrier' && invoiceInfo.phoneCarrierNumber)
      || (invoiceInfo.receiptType === 'business_einvoice'
        && invoiceInfo.businessUbn.length === 8)
    ) {
      setIsCheckoutDisabledConditions({ invoice: false })
      return
    }

    setIsCheckoutDisabledConditions({ invoice: true })
  }, [invoiceInfo, pickedPayment, setIsCheckoutDisabledConditions])

  const invoices: Invoices = {
    non_business_einvoice: t('onlineInvoice.nonBusiness'),
    phone_carrier: t('onlineInvoice.mobileCarrier'),
    business_einvoice: t('onlineInvoice.businessInvoice'),
  }

  const changeInvoiceType = (e: ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value !== ReceiptType.PHONE_CARRIER) {
      setPhoneCarrierNumber('')
    }
    setReceiptType(e.target.value as ReceiptType)
  }

  useEffectOnce(() => {
    if (invoiceInfo.receiptType) return
    const initialInvoiceType = () => {
      setReceiptType(ReceiptType.NON_BUSINESS_EINVOICE)
    }
    initialInvoiceType()
  })

  const businessUbnError = useMemo(() => invoiceError?.issues.find(issue => issue.path.includes('businessUbn'))?.message, [invoiceError])
  const phoneCarrierError = useMemo(() => invoiceError?.issues.find(issue => issue.path.includes('phoneCarrierNumber'))?.message, [invoiceError])

  if (!isInvoiceEnable || !company) {
    return null
  }

  const phoneCarrierField = (
    <CarrierInput
      value={invoiceInfo.phoneCarrierNumber}
      status={carrierStatus}
      label={<Text variant="body">手機條碼</Text>}
      errorMessage={phoneCarrierError}
      onChange={(carrierValue) => {
        setPhoneCarrierNumber(carrierValue)
      }}
    />
  )

  const businessEinvoiceFields = (
    <>
      <label className={style.field}>
        <VStack gap={1}>
          <span>{t('onlineInvoice.recipientName')}</span>
          <input
            type="text"
            value={invoiceInfo.businessUbnTitle}
            placeholder={t('onlineInvoice.defaultUBN') || ''}
            onChange={(e) => {
              setBusinessUbnTitle(e.target.value)
            }}
            maxLength={30}
          />
          <Text variant="caption" color={colors.grey500}>若留空，則自動帶入統一編號</Text>
        </VStack>
      </label>
      <label className={style.field}>
        <VStack gap={1}>
          <span>{t('onlineInvoice.UBN')}</span>
          <input
            type="text"
            value={invoiceInfo.businessUbn}
            placeholder={t('onlineInvoice.inputUBNPlaceholder') || ''}
            inputMode="numeric"
            onChange={(e) => {
              setBusinessUbn(e.target.value)
            }}
          />
          {businessUbnError && <Text variant="caption" color={colors.red600}>{businessUbnError}</Text>}
          <Text variant="caption" color={colors.grey500}>統一編號需為 8 碼半型數字</Text>
        </VStack>
      </label>
    </>
  )

  return (
    <VStack p={2} mt={2} gap={1}>
      <Text variant="subtitle">{t('onlineInvoice.EInvoiceRequest')}</Text>
      <VStack gap={3}>
        <select
          name="invoice"
          id="invoice"
          className={style.invoices}
          value={invoiceInfo.receiptType || ''}
          onChange={changeInvoiceType}
        >
          {Object.keys(invoices).map((invoice) => {
            return (
              <option value={invoice} key={invoice}>
                {invoices[invoice]}
              </option>
            )
          })}
        </select>
        {invoiceInfo.receiptType === 'phone_carrier' && phoneCarrierField}
        {invoiceInfo.receiptType === 'business_einvoice' && businessEinvoiceFields}
      </VStack>

      <label className={style['refund-statement']}>
        <input
          type="checkbox"
          checked={invoiceInfo.refundStatement}
          onChange={e => setRefundStatement(e.target.checked)}
        />
        <span>{t('onlineInvoice.invoiceHandlingAgreement', { company })}</span>
      </label>
    </VStack>
  )
}
